import React from 'react';
import { Calendar, Space, Tooltip } from 'antd';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { DayOfWeek, MonthlyType, Schedule, ScheduleType } from '../types';

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

interface ScheduleCalendarProps {
  schedules: Schedule[];
  onDateSelect: (date: dayjs.Dayjs, schedulesForDate: Schedule[]) => void;
  calendarMode: 'month' | 'year';
  onModeChange: (mode: 'month' | 'year') => void;
}

const ScheduleCalendar: React.FC<ScheduleCalendarProps> = ({
  schedules,
  onDateSelect,
  calendarMode,
  onModeChange,
}) => {
  const getScheduleTypeColor = (type: string) => {
    switch (type) {
      case 'Weekly':
        return '#52c41a';
      case 'BiWeekly':
        return '#1890ff';
      case 'Monthly':
        return '#722ed1';
      default:
        return '#d9d9d9';
    }
  };

  // Helper function to get the day name from a date
  const getDayName = (date: dayjs.Dayjs): string => {
    const dayNames = [
      'Sunday',
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
    ];
    return dayNames[date.day()];
  };

  // Helper function to convert DayOfWeek enum to day number (0-6, Sunday-Saturday)
  const getDayNumber = (dayOfWeek: DayOfWeek): number => {
    const dayMap = {
      [DayOfWeek.SUNDAY]: 0,
      [DayOfWeek.MONDAY]: 1,
      [DayOfWeek.TUESDAY]: 2,
      [DayOfWeek.WEDNESDAY]: 3,
      [DayOfWeek.THURSDAY]: 4,
      [DayOfWeek.FRIDAY]: 5,
      [DayOfWeek.SATURDAY]: 6,
    };
    return dayMap[dayOfWeek];
  };

  // Helper function to check if a date falls within a schedule's date range
  const isDateInScheduleRange = (
    date: dayjs.Dayjs,
    schedule: Schedule,
  ): boolean => {
    if (!schedule.startDate) return false;
    const start = dayjs(schedule.startDate).startOf('day');
    const end = schedule.endDate ? dayjs(schedule.endDate).endOf('day') : null;
    return (
      date.isSameOrAfter(start, 'day') &&
      (!end || date.isSameOrBefore(end, 'day'))
    );
  };

  // Helper function to check if a date matches a schedule's collection pattern
  const isCollectionDay = (date: dayjs.Dayjs, schedule: Schedule): boolean => {
    if (!isDateInScheduleRange(date, schedule) || !schedule.isActive) {
      return false;
    }

    const dayName = getDayName(date) as DayOfWeek;
    if (!schedule.startDate) return false;
    const scheduleStart = dayjs(schedule.startDate);
    const daysDiff = date.diff(scheduleStart, 'day');

    switch (schedule.scheduleType) {
      case ScheduleType.WEEKLY: {
        const weeklyConfig = schedule.collectionConfig.weekly;
        if (!weeklyConfig || !weeklyConfig.days.includes(dayName)) {
          return false;
        }
        return true;
      }

      case ScheduleType.BIWEEKLY: {
        const biWeeklyConfig = schedule.collectionConfig.biWeekly;
        if (!biWeeklyConfig) {
          return false;
        }

        const weeksDiff = Math.floor(daysDiff / 7);
        const isWeek1 = weeksDiff % 2 === 0;

        if (isWeek1) {
          return biWeeklyConfig.week1Days.includes(dayName);
        } else {
          return biWeeklyConfig.week2Days.includes(dayName);
        }
      }

      case ScheduleType.MONTHLY: {
        const m = schedule.collectionConfig.monthly;
        if (!m) return false;

        const dayName = getDayName(date) as DayOfWeek;
        const isOnDate =
          m.type === MonthlyType.ON_DATE ||
          ('date' in m && m.date && !('day' in m));
        const isOnDay =
          m.type === MonthlyType.ON_DAY || ('day' in m && 'ordinal' in m);

        if (isOnDate) {
          return 'date' in m ? date.date() === m.date : false;
        }

        if (isOnDay) {
          if (!('day' in m) || !('ordinal' in m)) return false;
          const wantedDay = m.day as DayOfWeek;
          if (dayName !== wantedDay) return false;

          const ordinal = String(m.ordinal || '').toUpperCase();

          const firstDayOfMonth = date.startOf('month');
          let firstOccur = firstDayOfMonth.day(getDayNumber(wantedDay));
          if (firstOccur.isBefore(firstDayOfMonth))
            firstOccur = firstOccur.add(7, 'day');

          if (ordinal === 'LAST') {
            const lastDay = date.endOf('month');
            let t = lastDay.day(getDayNumber(wantedDay));
            if (t.isAfter(lastDay)) t = t.subtract(7, 'day');
            return date.isSame(t, 'day');
          }

          const map: Record<string, number> = {
            FIRST: 0,
            SECOND: 1,
            THIRD: 2,
            FOURTH: 3,
          };
          if (!(ordinal in map)) return false;
          const t = firstOccur.add(map[ordinal] * 7, 'day');
          return date.isSame(t, 'day');
        }

        return false;
      }

      default:
        return false;
    }
  };

  // Get all schedules that apply to a specific date
  const getSchedulesForDate = (date: dayjs.Dayjs): Schedule[] => {
    return schedules.filter((schedule) => isCollectionDay(date, schedule));
  };

  // Handle date selection in calendar
  const handleDateSelect = (date: dayjs.Dayjs) => {
    const schedulesForDate = getSchedulesForDate(date);
    onDateSelect(date, schedulesForDate);
  };

  // Custom date cell renderer for calendar
  const dateCellRender = (date: dayjs.Dayjs) => {
    const schedulesForDate = getSchedulesForDate(date);

    if (schedulesForDate.length === 0) {
      return null;
    }

    return (
      <div style={{ position: 'relative', height: '100%', width: '100%' }}>
        <div
          style={{
            position: 'absolute',
            bottom: 2,
            right: 2,
            display: 'flex',
            flexDirection: 'row',
            gap: 2,
            flexWrap: 'wrap',
            maxWidth: '20px',
          }}
        >
          {schedulesForDate.slice(0, 4).map((schedule, index) => (
            <Tooltip
              key={schedule.id}
              title={`${schedule.scheduleType === ScheduleType.BIWEEKLY ? 'Fortnightly' : schedule.scheduleType} Schedule`}
              placement="top"
            >
              <div
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  backgroundColor: getScheduleTypeColor(schedule.scheduleType),
                  border: '1px solid #fff',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                  cursor: 'pointer',
                }}
              />
            </Tooltip>
          ))}
          {schedulesForDate.length > 4 && (
            <Tooltip
              title={`${schedulesForDate.length} schedules total`}
              placement="top"
            >
              <div
                style={{
                  width: 8,
                  height: 6,
                  fontSize: '8px',
                  color: '#666',
                  background: '#f0f0f0',
                  borderRadius: '2px',
                  padding: '0 1px',
                  lineHeight: '6px',
                  textAlign: 'center',
                  cursor: 'pointer',
                  border: '1px solid #ddd',
                }}
              >
                +{schedulesForDate.length - 4}
              </div>
            </Tooltip>
          )}
        </div>
      </div>
    );
  };

  // Month cell renderer (optional, for month view)
  const monthCellRender = (date: dayjs.Dayjs) => {
    const monthSchedules = schedules.filter((schedule) => {
      if (!schedule.startDate || !schedule.endDate) return false;
      const scheduleStart = dayjs(schedule.startDate);
      const scheduleEnd = dayjs(schedule.endDate);
      return (
        date.isSame(scheduleStart, 'month') ||
        date.isSame(scheduleEnd, 'month') ||
        (date.isAfter(scheduleStart, 'month') &&
          date.isBefore(scheduleEnd, 'month'))
      );
    });

    if (monthSchedules.length === 0) {
      return null;
    }

    return (
      <div style={{ fontSize: '12px', color: '#666' }}>
        {monthSchedules.length} schedule{monthSchedules.length > 1 ? 's' : ''}
      </div>
    );
  };

  return (
    <div>
      {/* Calendar Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <div style={{ fontSize: '16px', fontWeight: 500 }}>
          Collection Schedule Calendar
        </div>
        <div style={{ fontSize: '14px', color: '#666' }}>
          Total Schedules: {schedules.filter((s) => s.isActive).length} active,{' '}
          {schedules.filter((s) => !s.isActive).length} inactive
        </div>
      </div>

      {/* Legend */}
      <div
        style={{
          marginBottom: 16,
          padding: '12px',
          background: '#f9f9f9',
          borderRadius: '6px',
        }}
      >
        <div style={{ fontSize: '14px', fontWeight: 500, marginBottom: 8 }}>
          Schedule Types:
        </div>
        <Space wrap>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: '#52c41a',
                border: '1px solid #fff',
                boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
              }}
            />
            <span style={{ fontSize: '12px', fontWeight: 500 }}>Weekly</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: '#1890ff',
                border: '1px solid #fff',
                boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
              }}
            />
            <span style={{ fontSize: '12px', fontWeight: 500 }}>
              Fortnightly
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: '#722ed1',
                border: '1px solid #fff',
                boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
              }}
            />
            <span style={{ fontSize: '12px', fontWeight: 500 }}>Monthly</span>
          </div>
        </Space>
        <div style={{ fontSize: '12px', color: '#666', marginTop: 8 }}>
          💡 <strong>Tip:</strong> Click on dates with colored dots to view
          schedule details
        </div>
      </div>

      {/* Calendar */}
      <div
        style={{
          border: '1px solid #d9d9d9',
          borderRadius: '6px',
          background: '#fff',
        }}
      >
        <Calendar
          dateCellRender={dateCellRender}
          monthCellRender={monthCellRender}
          onSelect={handleDateSelect}
          mode={calendarMode}
          onPanelChange={(date, mode) => {
            onModeChange(mode);
          }}
          style={{
            fontSize: '12px',
          }}
        />
      </div>
    </div>
  );
};

export default ScheduleCalendar;
